import {
    IOccurrenceRule,
    NotificationComponentConfig,
    Query,
    UserDefinedNotificationConfig,
    UserDefinedNotificationQueryOptions,
} from '@apis/Notification/model';
import { QueryConstant, QueryExpr, QueryOperation } from '@apis/Resources';
import { UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { createAccessor } from '@root/Components/Settings/InputSpec';
import { FormElement } from '@root/Components/Settings/SettingsForms';
import { EventEmitter } from '@root/Services/EventEmitter';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { IInvoiceFieldCompatibilityLookup, InvoiceFieldCompatibilityService } from '@root/Services/Invoices/InvoiceFieldCompatibilityService';
import { InvoiceQueryDatasourceFactory } from '@root/Services/Invoices/InvoiceQueryDatasourceFactory';
import { IBaseInvoiceRecord, IMixedInvoiceRecord, InvoiceSchemaService } from '@root/Services/Invoices/InvoiceSchemaService';
import { ExprEvaluator } from '@root/Services/Query/ArrayDataSource';
import { QueryDatasource } from '@root/Services/Query/QueryDatasource';
import { exprBuilder, SchemaService } from '@root/Services/QueryExpr';
import { ArrowsUpDown, BorderAll, CalendarStats, ChartBar, Coin, Percentage, Settings } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { RecurrenceRuleType } from '../../Components/Schedule';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem, PrefixedParams } from './BasePresets';

// #region Threshold Query Builders

function buildAvgUsageChangeThresholdQuery(defaultDays: number) {
    const queryStep = (source: QueryExpr, queryValue: Record<string, any>, extractField?: string, mode?: string): QueryOperation => ({
        Operation: 'query',
        Operands: [
            source,
            { __cast: 'query', Value: queryValue },
            ...(extractField ? [{ Value: extractField }] : []),
            ...(mode ? [{ Value: mode }] : []),
        ],
    });

    const avgOfField = (field: string) => ({
        Select: [{ Alias: 'total', Expr: { Operation: 'avg', Operands: [{ Field: field }] } }],
    });

    const pSource = queryStep({ Field: '__pResults' }, { Sort: [{ Direction: 'Desc', Expr: { Field: 'Date' } }] });
    const pSelectedSet = queryStep({ Field: 'pSource' }, { Take: defaultDays });
    const pSelected = queryStep({ Field: 'pSelectedSet' }, avgOfField('Usage Cost'), 'total', 'single');
    const pOthersSet = queryStep({ Field: 'pSource' }, { Skip: defaultDays });
    const pOthers = queryStep(
        { Field: 'pOthersSet' },
        {
            Select: [
                { Alias: 'selected', Expr: { Field: 'pSelected' } },
                { Alias: 'total', Expr: { Operation: 'avg', Operands: [{ Field: 'Usage Cost' }] } },
            ],
        },
        'total',
        'single'
    );
    const pLeftOp = queryStep(
        { __cast: 'records', Value: [{}] } as any,
        {
            Select: [
                {
                    Alias: 'total',
                    Expr: {
                        Operation: 'divide',
                        Operands: [{ Field: 'pSelected' }, { Operation: 'add', Operands: [{ Field: 'pSelected' }, { Field: 'pOthers' }] }],
                    },
                },
            ],
        },
        'total',
        'single'
    );

    return {
        Select: [
            { Alias: 'selected', Expr: { Field: 'pSelected' } },
            { Alias: 'others', Expr: { Field: 'pOthers' } },
            { Alias: 'total', Expr: { Field: 'pLeftOp' } },
        ],
        Parameters: { pSource, pSelectedSet, pSelected, pOthersSet, pOthers, pLeftOp },
    };
}

function buildNewServiceThresholdQuery() {
    return {
        Select: [{ Alias: 'ct', Expr: { Operation: 'count', Operands: [] } }],
        Where: {
            Operation: 'and',
            Operands: [
                { Operation: 'ne', Operands: [{ Field: 'Sum Billed Cost-1759348136615' }, { Value: 0 }] },
                { Operation: 'eq', Operands: [{ Field: 'Cost' }, { Value: 0 }] },
            ],
        },
    };
}

function buildNewServiceAlertPresentation() {
    return {
        settings: [
            {
                optionId: 'grid-alert',
                description: 'Alert when any table row meets all of the conditions',
                model: {
                    filter: [
                        { Operation: 'ne', Operands: [{ Field: 'Sum Billed Cost-1759348136615' }, { Value: 0 }] },
                        { Operation: 'eq', Operands: [{ Field: 'Cost' }, { Value: 0 }] },
                    ],
                },
                brief: 'When any table rows match alert conditions',
            },
        ],
    };
}

function buildAggIfColumn(alias: string, monthField: string, dateOp: QueryOperation, costField: string, costType?: string) {
    const select: any = {
        Alias: alias,
        Expr: {
            Operation: 'aggif',
            Operands: [
                { Operation: 'eq', Operands: [{ Field: monthField }, dateOp] },
                { Operation: 'sum', Operands: [costType ? { Field: costField, Type: costType } : { Field: costField }] },
            ],
        },
    };
    return select;
}

function snapMonthStart(offset: number, unit: 'month' | 'day' = 'month') {
    return {
        Operation: 'addDate',
        Operands: [
            { Operation: 'snapDate', Operands: [{ Operation: 'currentDate', Operands: [] }, { Value: 'month' }, { Value: 'start' }] },
            { Value: offset },
            { Value: unit },
        ],
    };
}

// #endregion

class InvoiceDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(
        queryDatasource: QueryDatasource,
        schemaSvc: SchemaService,
        private readonly invoiceApi: InvoiceApiService,
        private readonly exprEvaluator: ExprEvaluator,
        private readonly fieldCompat: IInvoiceFieldCompatibilityLookup
    ) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);

        return this.createDatasourcePresets(
            name,
            <Icon />,
            [
                this.buildAvgUsageChangePreset({ days: 10, percent: 0.2, change: 'increase' }),
                this.buildAvgUsageChangePreset({ days: 10, percent: 0.2, change: 'decrease' }),
                this.buildAvgUsageChangePreset({ days: 30, percent: 0.2, change: 'increase' }),
                this.buildAvgUsageChangePreset({}),
                this.buildNewServiceAlert(),
                this.buildCustomInvoiceAlert(),
            ],
            [
                this.buildCostReport('usage', { days: 15, period: 'Weekly' }),
                this.buildCostReport('usage', { days: 30, period: 'Weekly' }),
                this.buildCostReport('total', { days: 15, period: 'Weekly' }),
                this.buildCostReport('total', { days: 30, period: 'Weekly' }),
                this.buildMonthToDateCostReport('usage', { recurrence: 'Weekly' }),
                this.buildMonthToDateCostReport('total', { recurrence: 'Weekly' }),
                this.buildCustomCostReport(),
            ]
        );
    }

    // #region Alerts

    private buildAvgUsageChangePreset(options: {
        days?: number;
        percent?: number;
        change?: 'increase' | 'decrease' | 'change';
    }): INotificationPresetItem<any> {
        const { days, percent, change } = options;
        const daysDef = days ?? 10;
        const changeDef = change ?? 'increase';
        const percentDef = percent ?? 0.2;

        const daysDesc = 'days' in options ? `${options.days}-day ` : 'configured days of';
        const percentLbl = ` ${percentDef * 100}%`;
        const percentDesc = percent === undefined ? 'by a configured percent' : `by ${percentLbl}`;
        const changeDirLbl = change === 'increase' ? 'up' : change === 'decrease' ? 'down' : 'change';
        const changeDirDesc = change === 'increase' ? 'increases' : change === 'decrease' ? 'decreases' : 'changes';

        type Params = PrefixedParams<Required<typeof options>>;

        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));
        const filterInput = this.createFilterSettings();

        return this.buildPreset({
            idParts: [days, 'day-usage', change, 'by', percent],
            icon: !days || !percent || !change ? <Settings /> : <ChartBar />,
            label: [
                days === undefined ? { type: 'variable', text: '##' } : days.toString(),
                '-day daily usage ',
                change === undefined ? { type: 'variable', text: 'up/down' } : changeDirLbl,
                percent === undefined ? { type: 'variable', text: '##%' } : percentLbl,
            ],
            description: `Alert when the ${daysDesc} daily usage cost ${changeDirDesc} ${percentDesc}`,
            initialize: (data) => {
                const xb = this.createExprBuilder<Params>();
                const groups = this.selectExprs({ Date: dateExpr });
                const values = this.selectExprs({ 'Usage Cost': xb.sum(costExpr) });
                const defaultDays = Math.round(daysDef / 4);

                return this.mergeConfig(
                    {
                        Title: 'Significant Usage Cost Change',
                        Description: '',
                        ComponentConfig: this.createBaseBarChartConfig(groups, values, {
                            xFormat: 'short-date',
                            format: 'money-with-two-decimals',
                            ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                            metricLabel: 'Usage Cost',
                            reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                        }),
                        Trigger: {
                            TriggerType: 'Threshold',
                            Threshold: {
                                ThrottleFrequency: 1,
                                ThrottlePeriod: 'Daily',
                                ThresholdQuery: data.Trigger?.Threshold?.ThresholdQuery ?? buildAvgUsageChangeThresholdQuery(defaultDays),
                                ThresholdCondition: data.Trigger?.Threshold?.ThresholdCondition ?? {
                                    Operation: changeDef === 'increase' ? 'gte' : 'lte',
                                    Operands: [{ Field: 'pLeftOp' }, { Value: percentDef }],
                                },
                                PresentationOptions: {
                                    settings: [{ description: 'Alert if usage cost changes significantly' }],
                                },
                            },
                        },
                        QueryOptions: {
                            Parameters: {
                                ...this.createUserInputParams(data, { pDays: daysDef, pPercent: percentDef, pChange: changeDef }),
                                pFrom: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.model.pDays.plus(1).times(-1), 'day'), 'day', 'start')),
                                pTo: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.param(-1), 'day'), 'day', 'end')),
                            },
                            Filters: [this.createUsageFilterExpr(), this.createDateFilter()],
                            DatasourceName: this.baseQueryDatasource.name,
                            ApiParameters: {
                                from: xb.resolve(xb.model.pFrom),
                                to: xb.resolve(xb.model.pTo),
                            },
                        },
                    },
                    data
                );
            },
            getForm: (data, h) => {
                type ParamExprs = {
                    [K in keyof PrefixedParams<Required<typeof options>>]: QueryConstant<PrefixedParams<Required<typeof options>>[K]>;
                };
                const params = data.QueryOptions!.Parameters! as unknown as ParamExprs;

                const elements = [] as FormElement[];
                const thresholdParams = data.Trigger!.Threshold!.ThresholdQuery!.Parameters as {
                    pSelectedSet: QueryOperation;
                    pOthersSet: QueryOperation;
                };
                const accTakeDays = createAccessor((t: typeof thresholdParams) => t.pSelectedSet.Operands![1].Value.Take as number);
                const accSkipDays = createAccessor((t: typeof thresholdParams) => t.pOthersSet.Operands![1].Value.Skip as number);
                const accCond = createAccessor((d: typeof data) => d.Trigger!.Threshold!.ThresholdCondition);
                const xb = this.createExprBuilder<{ pLeftOp: number }>();

                const updateQuery = () => {
                    const daysVal = Math.round((params.pDays?.Value ?? daysDef) / 4);
                    accTakeDays.set(thresholdParams, daysVal);
                    accSkipDays.set(thresholdParams, daysVal);
                    const percentVal = params.pPercent?.Value ?? percentDef;
                    const changeVal = params.pChange.Value ?? changeDef;
                    const condition =
                        changeVal === 'change'
                            ? xb.or(xb.model.pLeftOp.gte(percentVal), xb.model.pLeftOp.lte(percentVal))
                            : changeVal === 'increase'
                            ? xb.model.pLeftOp.gte(percentVal)
                            : changeVal === 'decrease'
                            ? xb.model.pLeftOp.lte(percentVal)
                            : null;
                    if (condition) accCond.set(data, xb.resolve(condition));
                };

                if (days === undefined) {
                    elements.push(
                        this.createInput<ParamExprs>()
                            .type('number')
                            .options({ label: 'Days', min: 7, max: 45, icon: <ChartBar /> })
                            .map((d) => d.pDays.Value)
                            .bind(params, updateQuery)
                    );
                }
                if (percent === undefined) {
                    elements.push(
                        this.createInput<ParamExprs>()
                            .type('number')
                            .options({ label: 'Percent', icon: <Percentage /> })
                            .map((d) => d.pPercent.Value)
                            .modelToView((v) => v * 100)
                            .viewToModel((v) => v / 100)
                            .bind(params, updateQuery)
                    );
                }
                if (change === undefined) {
                    elements.push(
                        this.createInput<ParamExprs>()
                            .type('string')
                            .options({
                                label: 'Change Type',
                                options: [
                                    { value: 'increase', label: 'Increase' },
                                    { value: 'decrease', label: 'Decrease' },
                                    { value: 'change', label: 'Increase or Decrease' },
                                ],
                                icon: <ArrowsUpDown />,
                            })
                            .presentation({ width: 150, align: 'center' })
                            .map((d) => d.pChange.Value)
                            .bind(params, updateQuery)
                    );
                }

                const chart = data.ComponentConfig as unknown as ChartConfig;
                const costFld = this.createCostFieldOptions<typeof chart>()
                    ?.map((d) => d.values[0].Expr.Operands[0].Field!)
                    .modelToView((v) => v)
                    .viewToModel((v) => v)
                    .bind(chart);
                if (costFld) elements.push(costFld);

                const filterSetting = filterInput(data, updateQuery);
                const settingForm = h.section('Alert Settings', {
                    header: { label: 'Usage cost alert settings' },
                    elements: h.descriptionInputs(data),
                });
                settingForm.elements.push(h.sectionItem('Data Options', ...h.concatSectionItems(elements, [filterSetting])));
                return [settingForm];
            },
        });
    }

    private buildNewServiceAlert(): INotificationPresetItem<any> {
        const filterInput = this.createFilterSettings();
        const chartInput = this.createCustomChartBehavior(true);
        const monthLookbackInput = this.createMonthLookbackInput();
        const [usageMonthField, serviceNameField, billedCostField] = this.getFields('UsageMonth', 'ServiceName', 'BilledCost');

        return this.buildPreset({
            idParts: ['cost', 'alert', 'customizeable', 'new-services'],
            icon: <BorderAll />,
            label: ['New Services Costs'],
            description: 'Alert when services costs are found in the current billing period, but did not appear in the previous period',
            initialize: (data) =>
                this.mergeConfig(
                    {
                        Title: 'New Service Costs',
                        Description: 'Alert when services costs are found in the current billing period, but did not appear in the previous period',
                        CooldownIncrement: 1,
                        CooldownPeriod: 'Weekly',
                        Trigger: {
                            TriggerType: 'Threshold',
                            MessageType: 'Email',
                            Threshold: {
                                ThrottleFrequency: 1,
                                ThrottlePeriod: 'Daily',
                                ThresholdQuery: buildNewServiceThresholdQuery(),
                                PresentationOptions: buildNewServiceAlertPresentation(),
                            },
                            Schedule: {
                                DefaultTimezoneId: 'America/Chicago',
                                OccurrenceRules: [
                                    {
                                        DaysOfWeek: [1],
                                        Increment: 1,
                                        Type: 'Weekly',
                                        TimeOffsetsInMinutes: [480],
                                        StartAt: '2025-10-01T05:00:00Z',
                                        EndAt: '2026-04-01T05:00:00Z',
                                        Date: '2025-10-02T05:00:00.000Z',
                                        DaysOfMonth: [0],
                                        SkipWeekends: true,
                                    } as IOccurrenceRule,
                                ],
                            },
                            PresentationOptions: {},
                        },
                        ComponentConfig: {
                            Type: 'Chart',
                            title: '',
                            chartType: 'grid',
                            datasourceName: 'cur',
                            filters: [],
                            groups: [],
                            values: [{ Alias: 'Cost', Expr: { Operation: 'sum', Operands: [{ Field: billedCostField }] } }],
                            settings: {
                                allowFilter: false,
                                columns: [
                                    {
                                        type: 'string',
                                        select: { Alias: 'Service Name', Expr: { Field: serviceNameField, Type: 'string' } },
                                        id: 'Service Category',
                                        formatter: 'string',
                                    },
                                    {
                                        type: 'number',
                                        select: buildAggIfColumn('Previous Month', usageMonthField, snapMonthStart(-1), billedCostField),
                                        id: 'Cost',
                                        formatter: 'money',
                                    },
                                    {
                                        type: 'number',
                                        select: buildAggIfColumn(
                                            'Current Month',
                                            usageMonthField,
                                            snapMonthStart(0, 'day'),
                                            billedCostField,
                                            'number'
                                        ),
                                        id: 'Sum Billed Cost-1759348136615',
                                        formatter: 'money',
                                    },
                                ],
                                state: {
                                    columns: [
                                        { id: 'Service Category', width: 160 },
                                        { id: 'Cost', width: 160 },
                                        { id: 'Sum Billed Cost-1759348136615', width: 160 },
                                    ],
                                    filters: [],
                                    sort: [],
                                },
                            },
                        },
                        QueryOptions: {
                            Parameters: {
                                pFrom: {
                                    Operation: 'snapDate',
                                    Operands: [
                                        {
                                            Operation: 'addDate',
                                            Operands: [
                                                { Operation: 'currentDate', Operands: [] },
                                                { Operation: 'multiply', Operands: [{ Field: 'pMonths' }, { Value: -1 }] },
                                                { Value: 'month' },
                                            ],
                                        },
                                        { Value: 'month' },
                                        { Value: 'start' },
                                    ],
                                },
                                pTo: {
                                    Operation: 'snapDate',
                                    Operands: [
                                        {
                                            Operation: 'addDate',
                                            Operands: [{ Operation: 'currentDate', Operands: [] }, { Value: -1 }, { Value: 'day' }],
                                        },
                                        { Value: 'month' },
                                        { Value: 'start' },
                                    ],
                                },
                                pMonths: { Value: 2 },
                            },
                            Filters: [],
                            DatasourceName: 'cur',
                            ApiParameters: { from: { Field: 'pFrom' }, to: { Field: 'pTo' } },
                        },
                        PresentationOptions: { presetId: 'cur-custom-alert' },
                        Disabled: false,
                    },
                    data
                ),
            getForm: (data, h) => {
                const configChanged = new EventEmitter<any>(data.ComponentConfig!);
                const chartSettings = chartInput(data, configChanged.emit);
                const monthsSetting = monthLookbackInput(data, () => configChanged.emit(data.ComponentConfig!));
                const filterSetting = filterInput(data, configChanged.emit);
                chartSettings.elements.push(h.sectionItem('Invoice Line Item Filters', filterSetting, { type: 'divider' }, monthsSetting));

                return [
                    chartSettings,
                    h.section(
                        'Alert Settings',
                        h.sectionItem('Title and Description', ...h.descriptionInputs(data)),
                        h.alertConditionSection(data, configChanged)
                    ),
                ];
            },
        });
    }

    private buildCustomInvoiceAlert(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();
        const filterInput = this.createFilterSettings();
        const chartInput = this.createCustomChartBehavior(true);
        const monthLookbackInput = this.createMonthLookbackInput();

        return this.buildCustomAlertPreset(
            (data) =>
                this.mergeConfig(
                    {
                        ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                        QueryOptions: {
                            ...this.defaultQueryOptions(this.baseQueryDatasource.name),
                            Parameters: { ...this.createMonthLookbackParams(), ...this.createUserInputParams(data, { pMonths: 1 }) },
                            ApiParameters: this.createApiParams(),
                        },
                        Trigger: this.defaultAlertTrigger(),
                    },
                    data
                ),
            (data, h) => {
                const configChanged = new EventEmitter(data.ComponentConfig!);
                const chartSettings = chartInput(data, configChanged.emit);
                const monthsSetting = monthLookbackInput(data, () => configChanged.emit(data.ComponentConfig!));
                const filterSetting = filterInput(data, configChanged.emit);
                chartSettings.elements.push(h.sectionItem('Invoice Line Item Filters', filterSetting, { type: 'divider' }, monthsSetting));

                return [
                    chartSettings,
                    h.section(
                        'Alert Settings',
                        h.sectionItem('Title and Description', ...h.descriptionInputs(data)),
                        h.alertConditionSection(data, configChanged)
                    ),
                ];
            }
        );
    }

    // #endregion

    // #region Reports

    private buildCostReport(
        costType: 'usage' | 'total' | 'user-defined',
        options: { days?: number; period?: RecurrenceRuleType }
    ): INotificationPresetItem<any> {
        const { days, period } = options;
        const periodDef = period ?? 'Weekly';
        const costDesc = costType === 'usage' ? 'Usage Cost' : costType === 'total' ? 'Total Cost' : 'Cost';
        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));

        return this.buildCostReportPreset({
            idParts: [days, `${costType}-cost`, period],
            icon: !days || !period ? <Settings /> : <ChartBar />,
            label: this.createPresetLabel(
                { value: period, placeholder: 'Periodic' },
                { value: days, placeholder: '##', suffix: '-day' },
                { value: costType === 'user-defined' ? undefined : costDesc, placeholder: 'Cost' }
            ),
            description: this.createPresetDescription({ value: period, placeholder: 'Periodic' }, 'report of usage cost over the previous', {
                value: days,
                placeholder: 'configurable number of',
                suffix: ' days',
            }),
            title: `${costDesc} Report`,
            defaultScheduleType: periodDef,
            createComponentConfig: (data) => {
                const groups = this.selectExprs({ Date: dateExpr });
                const values = this.selectExprs({ Amount: this.createExprBuilder().sum(costExpr) });
                const config = this.createBaseBarChartConfig(groups, values, {
                    xFormat: 'short-date',
                    format: 'money-whole',
                    ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                    metricLabel: 'Amount',
                    reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                });
                const xb = this.createExprBuilder();
                const [chargePeriodStartField] = this.getExprs<[Date]>(this.getFields('ChargePeriodStart'));
                config.filters = [
                    xb.resolve(chargePeriodStartField.onOrAfter(xb.addDate(xb.currentDate(), xb.param(-(days ?? 15)), 'day') as unknown as Date)),
                ];
                return config;
            },
            createQueryOptions: (data) => ({
                Parameters: { ...this.createMonthLookbackParams(), ...this.createUserInputParams(data, { pMonths: 1 }) },
                Filters: costType === 'total' ? [] : [this.createUsageFilterExpr()],
                DatasourceName: this.baseQueryDatasource.name,
                ApiParameters: this.createApiParams(),
            }),
        });
    }

    private buildMonthToDateCostReport(
        costType: 'usage' | 'total' | 'user-defined',
        options: { recurrence?: RecurrenceRuleType }
    ): INotificationPresetItem<any> {
        const recurrenceDef = options.recurrence ?? 'Weekly';
        const costDesc = costType === 'usage' ? 'Usage Cost' : costType === 'total' ? 'Total Cost' : 'Cost';
        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));

        return this.buildCostReportPreset({
            idParts: [`${costType}-cost`, options.recurrence],
            icon: <ChartBar />,
            label: this.createPresetLabel({ value: options.recurrence, placeholder: 'Periodic' }, 'month-to-date', {
                value: costDesc,
                placeholder: 'Cost',
            }),
            description: this.createPresetDescription(
                { value: options.recurrence, placeholder: 'Configurable' },
                'report of',
                costType === 'user-defined' ? 'configured' : costType,
                'cost for the current month'
            ),
            title: `${costDesc} Report`,
            defaultScheduleType: recurrenceDef,
            createComponentConfig: (data) => {
                const groups = this.selectExprs({ Date: dateExpr });
                const values = this.selectExprs({ Amount: this.createExprBuilder().sum(costExpr) });
                const config = this.createBaseBarChartConfig(groups, values, {
                    xFormat: 'short-date',
                    format: 'money-whole',
                    ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                    metricLabel: 'Amount',
                    reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                });
                const xb = this.createExprBuilder();
                const [chargePeriodStartField] = this.getExprs<[Date]>(this.getFields('ChargePeriodStart'));
                config.filters = [xb.resolve(chargePeriodStartField.onOrAfter(xb.snapDate(xb.currentDate(), 'month', 'start') as unknown as Date))];
                return config;
            },
            createQueryOptions: (data) => ({
                Parameters: { ...this.createMonthLookbackParams(), ...this.createUserInputParams(data, { pMonths: 0 }) },
                Filters: costType === 'total' ? [] : [this.createUsageFilterExpr()],
                DatasourceName: this.baseQueryDatasource.name,
                ApiParameters: this.createApiParams(),
            }),
        });
    }

    private buildCustomCostReport() {
        const xb = this.createExprBuilder();
        const [category, cost] = this.getExprs<[string, number]>(this.getFields('ServiceCategory', 'BilledCost'));
        const [dimExpr, metricExpr] = this.selectExprs({ category, cost: xb.sum(cost) });

        return this.buildCostReportPreset({
            idParts: ['custom', 'report'],
            icon: <Settings />,
            label: 'Custom Report',
            description: 'Custom report with configurable data and schedule',
            title: 'Untitled',
            defaultScheduleType: 'Weekly',
            createComponentConfig: () => this.createCustomChartConfig('pie', dimExpr, metricExpr),
            createQueryOptions: (data) => ({
                Parameters: { ...this.createMonthLookbackParams(), ...this.createUserInputParams(data, { pMonths: 1 }) },
                Filters: [],
                DatasourceName: this.baseQueryDatasource.name,
                ApiParameters: this.createApiParams(),
            }),
        });
    }

    private buildCostReportPreset(config: {
        idParts: Array<string | number | undefined | null>;
        icon: JSX.Element;
        label: string | Array<string | any>;
        description: string;
        title: string;
        defaultScheduleType: RecurrenceRuleType;
        createComponentConfig: (data: UserDefinedNotificationConfig) => NotificationComponentConfig;
        createQueryOptions: (data: UserDefinedNotificationConfig) => UserDefinedNotificationQueryOptions;
    }): INotificationPresetItem<any> {
        const monthLookbackInput = this.createMonthLookbackInput();
        const filterInput = this.createFilterSettings();
        const chartInput = this.createCustomChartBehavior(true);

        return this.buildPreset({
            idParts: config.idParts,
            icon: config.icon,
            label: config.label,
            description: config.description,
            initialize: (data) =>
                this.mergeConfig(
                    {
                        Title: config.title,
                        Description: '',
                        ComponentConfig: config.createComponentConfig(data),
                        QueryOptions: config.createQueryOptions(data) as any,
                        Trigger: this.defaultSchedTrigger(config.defaultScheduleType),
                    },
                    data
                ),
            getForm: (data, h) => {
                const chartSettings = chartInput(data);
                const monthsSetting = monthLookbackInput(data);
                const filterSetting = filterInput(data);
                chartSettings.elements.push(h.sectionItem('Invoice Line Item Filters', filterSetting, { type: 'divider' }, monthsSetting));

                return [
                    chartSettings,
                    h.section(
                        'Report Settings',
                        h.sectionItem('Title and Description', ...h.descriptionInputs(data)),
                        h.scheduleForm(data.Trigger?.Schedule!)
                    ),
                ];
            },
        });
    }

    // #endregion

    protected override getConfiguredSchemaCtx(def: UserDefinedNotificationConfig) {
        const source = (query: Query) => {
            const { ApiParameters, Parameters } = def.QueryOptions ?? {};
            const apiCtx = ApiParameters ? this.exprEvaluator.project(ApiParameters, { parameters: Parameters }) : {};
            return this.invoiceApi.query(query, apiCtx, false);
        };

        return {
            ...this.schemaCtx,
            queryDatasource: {
                ...this.baseQueryDatasource,
                get source() {
                    return source;
                },
            },
        };
    }

    // #region Shared helpers

    private createCostFieldOptions<T>() {
        const showbackFields = this.fieldCompat.getValidFields('BilledCost', 'AdjustedCashCost', 'AdjustedAmortizedCost');
        if (showbackFields.length > 1) {
            return this.createInput<T>()
                .type('string')
                .options({ label: 'Cost Field', icon: <Coin />, options: showbackFields.map((f) => ({ value: f.path, label: f.name })) })
                .presentation({ width: 150, align: 'center' });
        }
    }

    private createDateFilter() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date }>();
        const [dateField] = this.getFieldExprs('ChargePeriodStart');
        return xb.resolve(xb.and(xb.fromExpr<Date>(dateField).onOrAfter(xb.model.pFrom), xb.fromExpr<Date>(dateField).onOrBefore(xb.model.pTo)));
    }

    private createUsageFilterExpr() {
        const xb = this.createExprBuilder<{}>();
        return xb.resolve(
            xb.or(
                xb.model.ChargeCategory!.eq('Usage'),
                xb.model['lineItem/LineItemType']!.eq([
                    'Usage',
                    'SavingsPlanCoveredUsage',
                    'SavingsPlanNegation',
                    'DiscountedUsage',
                    'Discount',
                    'PrivateRateDiscount',
                    'EdpDiscount',
                ])
            )
        );
    }

    private createExprBuilder<TParams = {}>() {
        type Model = IMixedInvoiceRecord & TParams & { pFrom: Date; pTo: Date };
        const { builder } = exprBuilder<Model>();
        return builder;
    }

    private getFieldExprs(...fields: (keyof IBaseInvoiceRecord)[]) {
        return fields.map((f) => ({ Field: f }));
    }

    private getFields<T extends string[]>(...fields: string[]): T {
        return this.fieldCompat.getAvailableFields(...(fields as (keyof IBaseInvoiceRecord)[])) as T;
    }

    private createApiParams() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date }>();
        return { from: xb.resolve(xb.model.pFrom), to: xb.resolve(xb.model.pTo) };
    }

    private createMonthLookbackInput() {
        type MonthParam = { pMonths: QueryConstant<number> };
        const input = this.createInput<MonthParam>()
            .type('string')
            .options({
                label: 'Lookback Period',
                options: Array.from({ length: 13 }, (_, i) => ({
                    value: i.toString(),
                    label: i === 0 ? 'Current Month Only' : i === 1 ? '1 Month' : `${i} Months`,
                })),
                defaultValue: '1',
                tooltip: 'Select the number of prior months (including the current month) to include in the report',
                icon: <CalendarStats />,
            })
            .presentation({ align: 'center', width: 160 })
            .map((d) => d.pMonths.Value)
            .modelToView((v) => v.toString())
            .viewToModel((v) => parseInt(v));

        return (data: UserDefinedNotificationConfig, onChanged?: () => void) => input.bind(data.QueryOptions!.Parameters! as MonthParam, onChanged);
    }

    private createMonthLookbackParams() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date; pMonths: number }>();
        return {
            pFrom: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.model.pMonths.times(-1), 'month'), 'month', 'start')),
            pTo: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.param(-1), 'day'), 'month', 'start')),
        };
    }

    // #endregion
}

@injectable()
export class InvoiceDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(
        @inject(InvoiceSchemaService) private readonly schemaSvc: InvoiceSchemaService,
        @inject(ExprEvaluator) private readonly exprEvaluator: ExprEvaluator,
        @inject(InvoiceQueryDatasourceFactory) private readonly datasourceFactory: InvoiceQueryDatasourceFactory,
        @inject(InvoiceApiService) private readonly invoiceApi: InvoiceApiService,
        @inject(InvoiceFieldCompatibilityService) private readonly fieldCompat: InvoiceFieldCompatibilityService
    ) {}

    public getSupportedDatasources() {
        return [this.datasourceFactory.getMetadata()];
    }

    public async get(_: string) {
        const [schemaDetail, fieldCompat] = await Promise.all([
            this.schemaSvc.getDailySchemaTransformationDetail(),
            this.fieldCompat.getLookup('daily', { useLegacyFallbacks: true }),
        ]);
        const { types } = schemaDetail;
        const queryDatasource = await this.datasourceFactory.getDatasource((q) => this.invoiceApi.query(q, {}));
        const schemaSvc = SchemaService.create(types);
        return new InvoiceDatasourcePresetsBuilder(queryDatasource, schemaSvc, this.invoiceApi, this.exprEvaluator, fieldCompat);
    }
}
